import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";
import palette from "assets/theme/base/palette";

const ShareContentDialog = ({
  shareContent,
  setShareContent,
  closeShareContentHandler,
  shareContentTitle,
  learnerMetricsData,
  setLearnerMetricData,
  learnerId,
  t,
}) => {
  const [learnersRecommendations, setLearnerRecommendations] = useState(
    learnerMetricsData["recommendedcontent"]
  );
  const [recommend, setRecommend] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (learnersRecommendations.includes(shareContentTitle)) {
      setRecommend(false);
    }
  }, []);

  const recommendHandler = () => {
    setLoading(true);
    learnerMetricsData["recommendedcontent"] = [...learnersRecommendations, shareContentTitle];
    learnerMetricsData["eventkey"] = { event: "recommendcontent", value: shareContentTitle };
    call(`users/${learnerId}/contentmetrics`, "PUT", { body: learnerMetricsData })
      .then((res) => {
        if (res.status == 200) {
          setLearnerMetricData(learnerMetricsData);
          setShareContent(false);
        } else if (res.status == 440) {
          setShareContent(false);
        }
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
        setLoading(false);
      });
  };

  return (
    <Dialog
      open={shareContent}
      onClose={closeShareContentHandler}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      {/* Close button */}
      <MDBox
        onClick={closeShareContentHandler}
        sx={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          width: 36, height: 36, borderRadius: "50%",
          bgcolor: palette.neutral[100],
          border: `1px solid ${palette.neutral[200]}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          transition: "all 180ms ease",
          "&:hover": { bgcolor: palette.neutral[200] },
        }}
      >
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
        {/* Decorative icon */}
        <MDBox sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
          <MDBox sx={{ position: "absolute", top: -10, left: -18, color: palette.brand[400], fontSize: "18px", lineHeight: 1 }}>✦</MDBox>
          <MDBox sx={{ position: "absolute", bottom: 0, left: -12, color: "#f48326", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
          <MDBox sx={{ position: "absolute", top: 2, right: -20, color: "#9c27b0", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
          <MDBox sx={{ position: "absolute", bottom: -8, right: -16, color: palette.brand[400], fontSize: "14px", lineHeight: 1 }}>✦</MDBox>
          <MDBox sx={{
            width: 100, height: 100, borderRadius: "50%",
            bgcolor: recommend ? palette.brand[50] : "#e8f5e9",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon sx={{ fontSize: "48px !important", color: recommend ? palette.brand[500] : "#4caf50" }}>
              {recommend ? "send" : "check_circle"}
            </Icon>
          </MDBox>
        </MDBox>

        {/* Title */}
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary", mb: 1 }}>
          {recommend ? t("shareContent.title") : t("shareContent.shared")}
        </MDTypography>

        <Divider sx={{ width: 40, mx: "auto", mb: 2, borderBottomWidth: "2px", borderColor: palette.neutral[300] }} />

        {/* Description */}
        {recommend && (
          <MDTypography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.7 }}>
            {t("shareContent.recommend", { title: shareContentTitle })}
          </MDTypography>
        )}

        {/* Recommend button */}
        {recommend && (
          <MDButton
            onClick={recommendHandler}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: "12px", py: 1.5, mb: 1.5, fontSize: "0.85rem", letterSpacing: "0.06em" }}
          >
            {loading
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : <><Icon sx={{ mr: 1, fontSize: "18px !important" }}>send</Icon>{t("shareContent.buttonTitles.recommend")}</>
            }
          </MDButton>
        )}

        {/* Close button */}
        <MDButton
          onClick={closeShareContentHandler}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: "12px", py: 1.5, fontSize: "0.85rem",
            letterSpacing: "0.06em", borderColor: palette.neutral[300],
            color: palette.neutral[600],
            "&:hover": { bgcolor: palette.neutral[100], borderColor: palette.neutral[400] },
          }}
        >
          {t("shareContent.buttonTitles.close")}
        </MDButton>
      </MDBox>
    </Dialog>
  );
};

export default ShareContentDialog;
